import heyaFlightsMobile from '@/src/assets/png/heya-hero-bg-mobile.webp'
import heyaFlightsDesktop from '@/src/assets/png/heya-hero-bg.webp'
import heyaHotelsMobile from '@/src/assets/png/heya-hotel-hero-bg-mobile.webp'
import heyaHotelsDesktop from '@/src/assets/png/heya-hotel-hero-bg.webp'
import { Box } from '@mui/material'
import React from 'react'

export type HeyaHeroVariant = 'flights' | 'hotels'

// Both artworks place their subject on the inline-end side and bake their own light wash
// into the opposite side, so the RTL handling below is shared — only the sources differ.
const HERO_ARTWORK: Record<HeyaHeroVariant, { desktop: string; mobile: string }> = {
    flights: { desktop: heyaFlightsDesktop.src, mobile: heyaFlightsMobile.src },
    hotels: { desktop: heyaHotelsDesktop.src, mobile: heyaHotelsMobile.src }
}

/**
 * How far the photo extends below the hero copy, per product, in px: the gap down to the
 * search card plus half that card's initial height, so the photo finishes level with the
 * card's midline.
 *
 * Fixed rather than derived on purpose. The card's height is not stable — Multi City adds a
 * row and AI mode swaps the form out entirely — and the photo must not stretch with it.
 * Measured at 1280 and 1600, where both values hold.
 */
export const HEYA_HERO_BLEED: Record<HeyaHeroVariant, number> = {
    flights: 129,
    hotels: 149
}

interface HeyaHeroBackgroundProps {
    /**
     * Breakpoints this instance paints on. The hero spans the greeting and the search tabs,
     * which live in different components per breakpoint, so each caller claims its own.
     */
    display: Record<string, string>
    variant?: HeyaHeroVariant
    /** px to extend past the bottom of the band, so the photo can run under the search card. */
    bleed?: number
}

/**
 * Heya hero backdrop: photo layer plus the mobile fade into the page below it.
 *
 * Callers render this only under the Heya theme, so it holds no brand check of its own —
 * it renders in both a server and a client tree, where neither `next/headers` nor a hook
 * would work everywhere.
 *
 * IMPORTANT — no percentage colour stops in any gradient added here. `stylis-plugin-rtl`
 * rewrites stop percentages as `100 - x` for the RTL cache but keeps their original order,
 * so `(0%, 34%, 58%)` becomes `(100%, 66%, 42%)`. CSS then clamps each stop to the largest
 * preceding one, collapsing the gradient to its first colour — which once rendered as a
 * flat white sheet over the whole hero in Arabic. Two-stop gradients with no percentages
 * are immune.
 */
export const HeyaHeroBackground: React.FC<HeyaHeroBackgroundProps> = ({ display, variant = 'flights', bleed = 0 }) => {
    const artwork = HERO_ARTWORK[variant]
    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: `-${bleed}px`,
                overflow: 'hidden',
                display
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    // This is the LCP element on the pages it appears on, so the source PNGs
                    // are served as WebP: flights 68 KB / 18 KB, hotels 84 KB / 23 KB. The
                    // sources are not committed — regenerate from the design assets with
                    // sharp, full width at quality 86 and an 828px resize at 84 (chosen by
                    // measuring PSNR; both land at or above ~38 dB).
                    //
                    // `.src` is taken in HERO_ARTWORK because a static image import is a
                    // StaticImageData object, not a string — interpolating the object
                    // directly yields url("[object Object]"). Quoted because emotion
                    // silently drops the whole declaration for an unquoted url() containing
                    // commas or parens.
                    backgroundImage: {
                        xs: `url("${artwork.mobile}")`,
                        lg: `url("${artwork.desktop}")`
                    },
                    backgroundSize: 'cover',
                    // Mobile keeps the subject at the far edge so the copy sits over sky
                    // rather than across her face.
                    backgroundPosition: { xs: '82% top', lg: 'center' },
                    backgroundRepeat: 'no-repeat',
                    // Moves the subject to the side the copy vacates in Arabic.
                    'html[dir="rtl"] &': {
                        transform: 'scaleX(-1)'
                    },
                    maxHeight: "450px"
                }}
            />

            {/* Mobile: fade the photo into the page below it. Direction-agnostic, so Arabic
                needs no special case here. Desktop needs no wash — both artworks carry their
                own light area under the copy column. */}
            <Box
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgb(255, 255, 255))'
                }}
            />
        </Box>
    )
}
